/**
 * Parent resolution for assets the editor creates (spec 2026-08-22-02 §6,
 * revised by spec 2026-08-25-04 §2).
 *
 * **Containing owner first.** The natural answer to "where is this radiator?"
 * is *the room it was drawn in*, so point-in-polygon resolves the space under
 * the placement point and its owner asset is the first candidate. The ontology
 * still arbitrates: if the created type does not accept that owner's type as a
 * parent, the rule walks **up** the hierarchy to the nearest ancestor it does
 * accept — the pre-existing behaviour, now a fallback rather than the default.
 * Provisioning was widened so the common case works: every `trade_*` group that
 * allowed `unit` now also allows `room`.
 *
 * Rollups are unaffected — `parent_asset_ids` still aggregates the radiator to
 * the unit and the building.
 *
 * The containing room may itself be **pending** (drawn moments ago, no uid
 * yet). That is the interesting case, and it resolves to `parentKey`: the save
 * orchestrator creates the room first and hands its fresh uid to the child.
 *
 * **Openings are the carve-out.** A door or a window belongs to the wall it is
 * cut into, so its walk starts at the **wall-owning asset** and the containing
 * space is never consulted (spec 2026-08-25-04 §2: "Openings keep parenting to
 * the wall-owning asset"). Without it a door would parent to a room by
 * accident: its centre lies *on* a room boundary by construction, and
 * `containingSpace` counts a point on the edge as inside — so the moment
 * `trade_openings` gained `room`, every door drawn on a room's wall would have
 * been filed under the room instead of the wall's owner.
 *
 * With no allowed ancestor anywhere on the chain we parent to the editing
 * context asset and say so — the caller turns `fallback` into a validation
 * warning rather than silently misplacing the asset.
 *
 * **The walk stops at the layer's edge** unless a parent was picked by hand
 * (`confineToFrame`). Climbing past the frame anchor does not just choose a
 * different parent, it moves the geometry to a different *layer* — see the
 * option's own note.
 */
import type { Point } from './geometry'
import { containingSpace, nearestSpace, type SceneDoc } from './scene'
import { lotBaseTypeCode } from '../../../lib/asset-type-tree'

export interface AssetLite {
    uid: string
    name?: string
    typeCode?: string
    parentUid?: string
    /**
     * The asset carries the `auto_name` flag: its stored name was derived by
     * the server from its type and `room_number` (spec 2026-08-31-02 §2), so
     * the editor shows the *live* derivation while the two are being edited.
     */
    autoName?: boolean
}

export interface ParentResolution {
    parentUid: string
    /** True when no ancestor matched and the frame asset was used instead. */
    fallback: boolean
    /**
     * No ancestor of the drop point — inside the frame or above it — has a
     * type the created type accepts, so there is nowhere legal to file this.
     *
     * It exists because the API now enforces `possible_parent_codes` at write
     * time (spec 2026-09-18-01): a resolution the ontology refuses no longer
     * produces a slightly-odd parent, it produces a 422 that would swallow the
     * drawing silently. The caller must show it instead of saving.
     */
    ontologyRefused?: boolean
    /** The asset the walk started from, when a room contained the point. */
    startedFromUid?: string
    /**
     * The *pending* asset the new one parents to — set when the containing
     * space is being created by the same save and has no uid yet. `parentUid`
     * then holds the best-known fallback (the pending room's own parent), so a
     * caller that ignores `parentKey` still writes something sane.
     */
    parentKey?: string
}

export interface ResolveParentOptions {
    point: Point
    doc: SceneDoc
    /** Every candidate ancestor, keyed by uid. */
    assets: Map<string, AssetLite>
    /** `possible_parent_codes` of the type being created. Empty = unconstrained. */
    possibleParentCodes: string[]
    /** The editor's context asset (floor / unit) — the last resort. */
    contextAssetUid: string
    /**
     * Start the walk here instead of at the containing space — the openings
     * carve-out, where the wall's owner is the answer by construction. The
     * ontology check is unchanged: an owner whose type the created type does
     * not accept is still walked past.
     */
    startFromUid?: string
    /**
     * Take the containing space as the parent **without** asking the ontology.
     *
     * Automatic placement answers "where did I just drop this?", and the only
     * answer the user can verify is the room under the pointer. Walking past it
     * because a trade group was provisioned before `room` was a legal parent
     * files the asset somewhere they did not put it and cannot see — which is
     * exactly what a stale ontology does to every equipment on the plan. The
     * walk still arbitrates when no drawn space contains the point.
     */
    preferContainingSpace?: boolean
    /**
     * Never parent **above** the frame being edited.
     *
     * The walk is unbounded by nature: a radiator dropped outside every room
     * starts at the floor, does not find `floor` in `trade_hvac`'s parents, and
     * keeps climbing until it reaches the *building*. The asset is then anchored
     * on the building's frame rather than this floor's, so its footprint here is
     * a cross-layer one and the save writes an explicit registration for it —
     * one new "layer" in the panel per radiator, for geometry the user drew
     * directly on the layer already open.
     *
     * So: automatic placement never leaves the layer it was drawn on. The walk
     * still runs — a containing room, or a unit inside this floor, is a better
     * parent than the floor and stays cross-layer where that is genuinely what
     * it is — but the moment a candidate is an *ancestor* of the drawing
     * context, the context itself is the answer. Only an explicit pick in the
     * right panel can put a footprint on another layer, which is exactly the
     * gesture that means "this belongs to the building, I am only drawing it
     * here".
     */
    confineToFrame?: boolean
    /**
     * Resolve the space by **distance** rather than by containment, within this
     * many millimetres (spec 2026-08-30-03 §17). A window belongs to the room
     * it lights, and its centre sits on that room's boundary — where
     * point-in-polygon answers by accident of tracing. Beyond the tolerance
     * there is no space and the walk decides, which with `confineToFrame` means
     * the floor.
     */
    nearestSpaceToleranceMm?: number
    /** Hierarchy walk guard. */
    maxDepth?: number
}

export function resolveCreatedAssetParent(opts: ResolveParentOptions): ParentResolution {
    const { assets, contextAssetUid } = opts
    const allowed = new Set(opts.possibleParentCodes ?? [])

    // Where do we start? An explicit anchor (the openings carve-out) wins;
    // otherwise the space containing the point — its *owner asset* is the room
    // — else the editing context.
    const anchored = opts.startFromUid !== undefined
    const space = anchored
        ? undefined
        : opts.nearestSpaceToleranceMm !== undefined
            ? nearestSpace(opts.doc, opts.point, opts.nearestSpaceToleranceMm)
            : containingSpace(opts.doc, opts.point)
    const pending = space?.owner.newAsset
    const startUid = opts.startFromUid ?? space?.owner.assetUid ?? pending?.parentUid ?? contextAssetUid
    const startedFromUid = opts.startFromUid ?? space?.owner.assetUid

    // The space under the point is the answer, and the ontology does not get a
    // vote (see `preferContainingSpace`) — as long as the API would accept it.
    // Since the write-time enforcement of `possible_parent_codes` (spec
    // 2026-09-18-01), a room the created type does not accept is not a
    // slightly-odd parent any more but a guaranteed 422, so the preference
    // yields to the walk below rather than saving something that cannot be
    // stored.
    const spaceOwnerType = space?.owner.assetUid ? assets.get(space.owner.assetUid)?.typeCode : undefined
    const spaceAccepted = allowed.size === 0
        || (space?.owner.newAsset ? allowed.has(space.owner.newAsset.typeCode) : Boolean(spaceOwnerType && allowed.has(spaceOwnerType)))
    if (opts.preferContainingSpace && space && spaceAccepted) {
        if (space.owner.assetUid) {
            return { parentUid: space.owner.assetUid, fallback: false, startedFromUid }
        }
        if (pending) {
            return {
                parentUid: pending.parentUid ?? contextAssetUid,
                parentKey: pending.key,
                fallback: false,
                startedFromUid,
            }
        }
    }

    // A room drawn in this same session has no uid yet. Its type is known, so
    // the ontology check is the same one; only the linkage differs.
    if (pending && !space?.owner.assetUid) {
        if (allowed.size === 0 || allowed.has(pending.typeCode)) {
            return {
                parentUid: pending.parentUid ?? contextAssetUid,
                parentKey: pending.key,
                fallback: false,
                startedFromUid,
            }
        }
    }

    if (allowed.size === 0) {
        // Unconstrained type: the containing room (or the context) is as good
        // a parent as the ontology allows us to know about.
        return { parentUid: startUid, fallback: false, startedFromUid }
    }

    const maxDepth = opts.maxDepth ?? 12

    // Everything strictly above the drawing context — the candidates
    // `confineToFrame` refuses. The start is exempt: an explicit anchor (the
    // opening's wall owner) is a statement about *this* asset, not a step the
    // walk took, so a door cut into a wall the building owns still belongs to
    // the building.
    const aboveContext = new Set<string>()
    if (opts.confineToFrame) {
        let up = assets.get(contextAssetUid)?.parentUid
        for (let i = 0; i < maxDepth && up; i++) {
            if (aboveContext.has(up)) break
            aboveContext.add(up)
            up = assets.get(up)?.parentUid
        }
        aboveContext.delete(startUid)
    }

    let cursor: string | undefined = startUid
    const seen = new Set<string>()
    for (let i = 0; i < maxDepth && cursor; i++) {
        if (seen.has(cursor) || aboveContext.has(cursor)) break
        seen.add(cursor)
        const asset = assets.get(cursor)
        if (asset?.typeCode && allowed.has(asset.typeCode)) {
            return { parentUid: asset.uid, fallback: false, startedFromUid }
        }
        cursor = asset?.parentUid
    }
    // Confined, landing on the frame is the rule and not a failure to place:
    // the user dropped this on the layer they had open, and that is where it
    // goes. Unconfined it is the last resort, and the caller turns it into a
    // "could not place this" warning.
    //
    // The walk deliberately does NOT climb past the frame to find a type the
    // ontology accepts. That was tried (spec 2026-09-18-01) and it broke the
    // contract three specs encode — "an equipment dropped outside every room
    // stays on the layer it was drawn on": a radiator on bare floor ended up
    // owned by the building, one cross-layer registration per radiator. The
    // ontology was the thing that was wrong, and `floor` is now an accepted
    // parent of the trade families that sit on one.
    const frameType = assets.get(contextAssetUid)?.typeCode
    if (allowed.size === 0 || (frameType && allowed.has(frameType))) {
        return { parentUid: contextAssetUid, fallback: !opts.confineToFrame, startedFromUid }
    }

    // Nowhere legal at all: not the chain, not the frame. The API would refuse
    // the write (422 since spec 2026-09-18-01), so the caller shows this
    // instead of saving — see PlanDiff.refusedAssets.
    return { parentUid: contextAssetUid, fallback: true, ontologyRefused: true, startedFromUid }
}

/** The asset types that anchor a frame (spec 2026-08-25-03 §1). */
export const FRAME_ANCHOR_TYPE_CODES = ['building_group', 'building', 'floor', 'unit']

/**
 * Does geometry **owned by** an asset of this type anchor a frame of its own?
 *
 * The question the split tool has to ask before it decides whether a drawn
 * outline needs a cross-layer registration (spec 2026-08-30-01 §7): a `unit`
 * anchors, so its outline traced on a floor is a footprint on someone else's
 * layer and the row is what says which; a `common_area` does not, so its
 * outline resolves to the floor by the ordinary implicit rule and a row would
 * be the identity registration `useSavePlan` warns about.
 */
export const typeAnchorsFrame = (typeCode: string | undefined): boolean =>
    typeCode !== undefined && FRAME_ANCHOR_TYPE_CODES.includes(lotBaseTypeCode(typeCode))

/**
 * The frame an asset's geometry lives in **implicitly**: its nearest
 * ancestor-or-self whose type anchors a frame (spec 2026-08-25-03 §1).
 * `undefined` when the chain runs out — an asset floating outside any frame.
 */
export function implicitFrameUid(
    assetUid: string | undefined,
    assets: Map<string, AssetLite>,
    maxDepth = 12,
): string | undefined {
    let cursor = assetUid
    const seen = new Set<string>()
    for (let i = 0; i < maxDepth && cursor; i++) {
        if (seen.has(cursor)) break
        seen.add(cursor)
        const asset = assets.get(cursor)
        if (asset?.typeCode && FRAME_ANCHOR_TYPE_CODES.includes(lotBaseTypeCode(asset.typeCode))) return asset.uid
        cursor = asset?.parentUid
    }
    return undefined
}

/**
 * Does drawing this asset on `frameUid` need an **explicit** registration?
 *
 * Only when its implicit frame is somewhere else (spec 2026-08-25-04 §1): the
 * building's elevator drawn on floor 3 is a cross-layer footprint, and the row
 * is what says *which layer*. Everything else resolves from the hierarchy and
 * a row would be ceremony. An asset we cannot place at all is treated as local
 * — writing a spurious row is worse than trusting the implicit rule.
 */
export function needsExplicitRegistration(
    assetUid: string | undefined,
    assets: Map<string, AssetLite>,
    frameUid: string,
): boolean {
    if (!assetUid || assetUid === frameUid) return false
    const frame = implicitFrameUid(assetUid, assets)
    return frame !== undefined && frame !== frameUid
}

/** The asset-type facts the parent rule needs, flattened from the API shape. */
export interface AssetTypeNode {
    uid: string
    code?: string
    /** `inherits_from_asset_type_uid` — an equipment leaf points at its trade. */
    inheritsFromUid?: string
    /** `possible_parent_asset_type_uids` (only returned with the `include` flag). */
    possibleParentUids?: string[]
}

/**
 * The parent codes a type accepts, resolved **through inheritance** — in both
 * directions.
 *
 * *Up*, because the ontology declares `possible_parent_codes` on the trade, not
 * on every leaf: `hvac_radiator` carries none and inherits `building | unit |
 * common_area | space_parking` from `trade_hvac` (spec 2026-08-22-02 §6's own
 * example). Taking the leaf's empty list at face value would make a radiator
 * parent to the room it was drawn in.
 *
 * *Down*, because the list is matched by code equality and the ontology
 * sub-types its locations: a trade that accepts `room` accepts a kitchen (spec
 * 2026-08-31-01 §5). Without the expansion, retyping a room from the panel's
 * select would silently re-parent everything dropped in it afterwards to the
 * unit above — a rule change nobody asked for, triggered by a label.
 */
export function possibleParentCodes(
    typeCode: string,
    byCode: Map<string, AssetTypeNode>,
    byUid: Map<string, AssetTypeNode>,
    maxDepth = 8,
): string[] {
    let node = byCode.get(typeCode)
    const seen = new Set<string>()
    for (let i = 0; i < maxDepth && node; i++) {
        if (seen.has(node.uid)) break
        seen.add(node.uid)
        const uids = node.possibleParentUids ?? []
        if (uids.length) {
            const codes = uids.map((uid) => byUid.get(uid)?.code).filter((c): c is string => Boolean(c))
            return withSubTypes(codes, byUid, maxDepth)
        }
        node = node.inheritsFromUid ? byUid.get(node.inheritsFromUid) : undefined
    }
    return []
}

/**
 * `codes` plus every type that inherits from one of them, transitively.
 *
 * Walks the whole catalog once per level rather than indexing children: the
 * list is a handful of codes and the catalog is ~130 types, and the levels are
 * bounded by `maxDepth` the same way the upward walk is.
 */
function withSubTypes(codes: string[], byUid: Map<string, AssetTypeNode>, maxDepth: number): string[] {
    const out = new Set(codes)
    const uidsOf = new Set(
        [...byUid.values()].filter((n) => n.code && out.has(n.code)).map((n) => n.uid),
    )
    for (let depth = 0; depth < maxDepth; depth++) {
        let grew = false
        for (const node of byUid.values()) {
            if (!node.inheritsFromUid || uidsOf.has(node.uid)) continue
            if (!uidsOf.has(node.inheritsFromUid)) continue
            uidsOf.add(node.uid)
            if (node.code) out.add(node.code)
            grew = true
        }
        if (!grew) break
    }
    return [...out]
}
